import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, MessageCircle, Calendar } from 'lucide-react';
import { ActivityItem as ActivityItemType } from '../../../services/activityService';
import { PopulatedDatabaseQuestion, PopulatedDatabaseAnswer } from '../../../types/types';

interface ActivityItemProps {
  activity: ActivityItemType;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  const { type, content, parentQuestion } = activity;

  // Format date to show relative time (e.g. "2 hours ago")
  const formatRelativeTime = (dateValue: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateValue.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
  };

  // For question activity items
  if (type === 'question') {
    const question = content as PopulatedDatabaseQuestion;

    return (
      <div className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary'>
            <MessageSquare size={18} />
          </div>

          <div className='flex-1'>
            <div className='flex flex-wrap justify-between gap-2 mb-1'>
              <Link
                to={`/user/${question.askedBy}`}
                className='font-medium text-blue-600 hover:underline'>
                {question.askedBy}
              </Link>

              <div className='flex items-center text-sm text-muted-foreground'>
                <Calendar size={14} className='mr-1' />
                {formatRelativeTime(new Date(question.askDateTime))}
              </div>
            </div>

            <div className='mb-2'>
              <span className='text-sm text-muted-foreground'>asked a question</span>
            </div>

            <Link
              to={`/question/${question._id}`}
              className='text-lg font-medium hover:text-primary transition-colors'>
              {question.title}

              <p className='mt-2 text-muted-foreground line-clamp-2'>{question.text}</p>

              <div className='mt-3 flex flex-wrap gap-2'>
                {question.tags.map(tag => (
                  <span
                    key={tag._id.toString()}
                    className='px-2 py-1 bg-primary/10 text-primary text-xs rounded-full'>
                    {tag.name}
                  </span>
                ))}
              </div>
            </Link>

            <div className='mt-3 flex items-center text-sm text-muted-foreground'>
              <div className='flex items-center mr-4'>
                <MessageCircle size={14} className='mr-1' />
                {question.answers.length} answer{question.answers.length !== 1 ? 's' : ''}
              </div>
              <div>
                {question.views.length} view{question.views.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For answer activity items
  if (type === 'answer') {
    const answer = content as PopulatedDatabaseAnswer;

    if (!parentQuestion) {
      return null; // Handle case where parent question is not available
    }

    return (
      <div className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600'>
            <MessageCircle size={18} />
          </div>

          <div className='flex-1'>
            <div className='flex flex-wrap justify-between gap-2 mb-1'>
              <Link
                to={`/user/${answer.ansBy}`}
                className='font-medium text-blue-600 hover:underline'>
                {answer.ansBy}
              </Link>

              <div className='flex items-center text-sm text-muted-foreground'>
                <Calendar size={14} className='mr-1' />
                {formatRelativeTime(new Date(answer.ansDateTime))}
              </div>
            </div>

            <div className='mb-2'>
              <span className='text-sm text-muted-foreground'>answered a question</span>
            </div>

            <Link
              to={`/question/${parentQuestion._id}`}
              className='text-lg font-medium hover:text-primary transition-colors'>
              {parentQuestion.title}
            </Link>

            <div className='mt-2 p-3 border-l-2 border-green-200 bg-green-50 rounded'>
              <p className='text-muted-foreground line-clamp-3'>{answer.text}</p>
            </div>

            <div className='mt-3 flex items-center text-sm text-muted-foreground'>
              <div className='flex items-center mr-4'>
                <MessageCircle size={14} className='mr-1' />
                {answer.comments.length} comment{answer.comments.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ActivityItem;
